<script lang="ts" setup>
// Без этого бесполезного импорта отвалится маска в инпуте телефона
import { vMaska } from 'maska'
import { Field as VeeField, useField } from 'vee-validate'
import { format } from 'date-fns'

import useVkAuth from '~/composables/useVkAuth'

import { useAppStore } from '~/store/app/app'
import { useUserStore } from '~/store/user/user'
import { useLoginStore } from '~/store/login/login'
import { useCityStore } from '~/store/city/city'

/* Utils */
import { sanitizePhoneInput } from '@/utils/input'

import { CurrentForm } from '~/store/login/login.types'
import { BadRequestError, Success, TooManyRequestError } from '~/api/results'
import { VkAuthType } from '@/services/vkAuth/vkAuth.types'

const { setVkFrame } = useVkAuth(VkAuthType.BUTTON)

const { showSnackbar } = useAppStore()
const { requestCode } = useUserStore()
const loginStore = useLoginStore()
const {
  cityInfo: { tel_input_placeholder },
  optionsPhone: phoneOptions,
} = useCityStore()
const { name: routeName } = useRoute()

// Форма с полем phone
const form = ref({
  phone: {
    label: 'Номер телефона *',
    placeholder: tel_input_placeholder,
    code: phoneOptions?.code,
    mask: phoneOptions?.mask,
    length: phoneOptions?.length,
    rules: 'phone',
  },
})

const phoneWithoutMask = computed(() =>
  loginStore.getPhoneWithoutMask(phoneOptions.code),
)
const requestCodeState = computed(() => loginStore.actions.requestCode)
const buttonDisabled = computed(
  () =>
    requestCodeState.value.disabled ||
    form.value.phone.mask?.length !== loginStore.phone.length,
)
const phoneValue = computed({
  get(): string {
    return loginStore.phone
  },
  set(value: string) {
    loginStore.setPhone(sanitizePhoneInput(value, phoneOptions.code))
  },
})

// Опции для маски номера телефона
const options = ref({
  mask: phoneOptions.mask,
  eager: true,
})

// Переход на компонент со входом по паролю
const loginWithPasswordHandler = () => {
  loginStore.changeCurrentForm(CurrentForm.SIGN_IN)
}

const formatTime = computed(() =>
  format(new Date(0, 0, 0, 0, 0, requestCodeState.value.timer), 'mm:ss'),
)

const requestCodeHandler = async (setErrors: Function) => {
  // Обработка нажатия enter у формы
  if (buttonDisabled.value) return

  loginStore.setRequestCodeState({ inProgress: true })

  const result = await requestCode({
    phone: phoneWithoutMask.value,
    ui_element: routeName || '',
  })

  if (result instanceof Success) {
    loginStore.startRequestCodeTimer(result.data.waitSeconds)
    loginStore.changeCurrentForm(CurrentForm.CONFIRM_CODE)
  } else if (result instanceof BadRequestError) {
    // Независимо от наименования ошибки, получаем текст из массива
    const errorMessage = result.data[Object.keys(result.data)[0]]

    setErrors(errorMessage)
  } else if (result instanceof TooManyRequestError) {
    showSnackbar({
      message: result.message,
    })

    loginStore.startRequestCodeTimer(result.timeout)
  } else {
    showSnackbar({
      message: 'Что-то пошло не так, попробуйте позже',
    })
  }

  loginStore.setRequestCodeState({ inProgress: false })
}

onMounted(() => {
  const blockForVkButton = document.querySelector('.vk-button')

  if (blockForVkButton) {
    setVkFrame(blockForVkButton)
  }
})

const phoneRequireds = () => {
  if (!phoneValue.value) return 'Поле не заполнено'

  if (phoneWithoutMask.value.length !== 11)
    return 'Некорректный формат номера телефона'

  return true
}

const { errorMessage, handleChange, setErrors } = useField(
  'phone',
  phoneRequireds,
  {
    validateOnValueUpdate: false,
  },
)

// Создаем обработчик валидатор для номера телефона
const validationListeners = computed(() => {
  if (!errorMessage.value) {
    return {
      blur: handleChange,
      change: handleChange,
      input: (e: Event) => handleChange(e, false),
    }
  }
  return {
    blur: handleChange,
    change: handleChange,
    input: handleChange,
  }
})
</script>

<template>
  <v-form class="form" @submit.prevent="requestCodeHandler">
    <h1 class="form__title">Введите номер, чтобы продолжить</h1>

    <label class="form__label">{{ form.phone.label }}</label>

    <vee-field
      v-slot="{ field }"
      v-model="phoneValue"
      name="phone"
      v-on="validationListeners"
    >
      <app-input
        v-maska:[options]
        v-bind="field"
        :placeholder="form.phone.placeholder"
        :prefix="`+${form.phone.code}`"
        :error-messages="errorMessage"
        class="form__input phone"
        autofocus
        height="20px"
        autocomplete="off"
        type="tel"
      />

      <div class="form__buttons">
        <app-button
          :loading="requestCodeState.inProgress"
          class="button request-code"
          :disabled="buttonDisabled"
          block
          @click="requestCodeHandler(setErrors)"
        >
          <span v-if="requestCodeState.timer > 0" class="timer">
            Запросить код повторно через
            {{ formatTime }}
          </span>
          <span v-else>Получить код</span>
        </app-button>
        <app-button
          class="button login-with-password"
          dimmed
          block
          @click="loginWithPasswordHandler"
        >
          У меня есть пароль
        </app-button>
      </div>
    </vee-field>

    <div class="vk-button"></div>

    <div class="form__policy">
      <span>Нажимая на кнопку, вы даете согласие на</span>
      <NuxtLink class="link" to="/terms_of_use/">
        обработку персональных данных
      </NuxtLink>
    </div>
  </v-form>
</template>

<style lang="scss" scoped>
.form {
  max-width: 320px;
  margin: auto;

  &__title {
    margin-bottom: 20px;
    font-size: 20px;
    font-weight: 800;
    text-align: center;
  }

  &__label {
    display: block;
    margin-bottom: 4px;
    font-size: 14px;
    font-weight: 600;
  }

  &__input {
    margin-bottom: 8px;
  }

  &__buttons {
    margin-bottom: 20px;

    .button {
      height: 50px;
      font-size: 14px;
      font-weight: 700;
      line-height: 19px;
      text-transform: none;
      letter-spacing: 0;
      border-radius: 10px;
      box-shadow: 0 4px 20px rgba(255, 80, 0, 0.16);

      &:not(:last-child) {
        margin-bottom: 28px;
      }
    }

    .login-with-password {
      color: $primary !important;
      background-color: $dimmed-grey !important;
      box-shadow: unset !important;
    }
  }

  &__policy {
    font-size: 12px;
    font-weight: 600;
    color: $unactive-grey;
    text-align: center;

    .link {
      color: $text-black;
    }
  }
}
</style>
